import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useBoolean } from 'react-hanger';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { CompositeFilterDescriptor, orderBy } from '@progress/kendo-data-query';
import useGridCustomCell, { INamedItem } from '@components-clean/core/grid/custom-cell';
import Grid from '@components-clean/core/grid-fe';
import { DateTimeCell } from '@components-clean/core/grid-fe/custom-cells';
import { GridColumnProps } from '@components-clean/core/grid-fe/grid.interface';
import { PopOutPage } from '@components-clean/core/page-toolbar/page-toolbar';
import { ControlsHubPanelsAssociatedToRisk } from '@interfaces/central-monitoring';
import { IRefreshGridEventBusMessage, REFRESH_GRID } from '@pages-clean/risk-assessment/constants';
import AssignCmControlChartsToRiskDialog from '@pages-clean/risk-assessment/risk-log/dialogs/assign-cm-control-charts-dialog';
import UnassignCmControlDialog from '@pages-clean/risk-assessment/risk-log/dialogs/unassign-cm-control-dialog';
import { RiskLogContext } from '@pages-clean/risk-assessment/risk-log/interfaces/context';
import { statusRAGChips } from '@pages-clean/risk-assessment/risk-log/sections/childArea/cm-controls/helper';
import { useEventBus } from '@utils-clean/eventBus';
import { PersistentStorageKeys } from '@utils-clean/persistent-storage-keys';
import { AuditApplicationArea, AuditDataType, AuditServiceType } from 'types/graphql-global-types';
import { Permissions } from '@contexts/user-context';
import { useAuthorization } from '@hooks/index';
import Constants from '@utils/constants';
import { stringDateWithoutTimeFromCurrentUtcDate } from '@utils/convertor';
import NotificationService, { ShowError, ShowSuccess } from '@utils/notification';
import { AllPaths } from '@utils/router/all-paths';
import { getControlChartsForRisk } from '@queries/generated/risk-assessment.generated';
import { GET_CM_CONTROLS_CHARTS } from '@queries/risk-assessment';
import ControlsHubApi from '@apis/bff-api/central-monitoring/controls-hub';

const CmControlsArea = () => {
    const user = useAuthorization();
    const canView = user?.can?.({
        module: Constants.Modules.RAM,
        page: Constants.PageElements.CMControls,
        permission: Permissions.Enabled,
        element: Constants.PageElements.CMControls,
    });

    if (!canView) return;

    const canEdit = user?.canWrite?.({
        module: Constants.Modules.RAM,
        element: Constants.DataElements.RiskAssessment,
        permission: Permissions.ReadWrite,
    });

    const { current } = useContext(RiskLogContext);
    const { study } = current;
    const { ChipCell, ActiveInactiveCell, TooltipCell } = useGridCustomCell();
    const risk = current.selectionState.selectedItem;
    const { t } = useTranslation('ram', { keyPrefix: 'riskLog' });
    const { t: tCommon } = useTranslation('common');
    const chips = useMemo(() => statusRAGChips(t), []);
    const exportFileName = t('grids.cmControls.exportFileName');
    const [panels, setPanels] = useState<ControlsHubPanelsAssociatedToRisk[]>([]);
    const { trigger: getPanels, isMutating } = ControlsHubApi.useGetPanelsAssociatedToRisk(study?.id);
    const [showAssignCmChartsDialog, setShowAssignCmChartsDialog] = useState(false);
    const showUnassignCmControlDialog = useBoolean(false);
    const [selectedControl, setSelectedControl] = useState<ControlsHubPanelsAssociatedToRisk>();

    const [filterState, setFilterState] = useState<CompositeFilterDescriptor | undefined>({
        logic: 'and',
        filters: [],
    });

    const { data: getCmControlChartsForRisk, loading: cmControlChartsLoading } = useQuery<getControlChartsForRisk>(
        GET_CM_CONTROLS_CHARTS,
        {
            variables: { studyId: study!.id },
        },
    );

    const getCmPanels = (panelIds: string[]) =>
        getPanels(panelIds ?? [])
            .then((data) => {
                setPanels(
                    orderBy(
                        data.map((item) => {
                            return {
                                ...item,
                                plotTitle: {
                                    active: item.active,
                                    name: item.name,
                                },
                                riskRecordId: risk?.recordId,
                                riskStatement: risk?.statement,
                            };
                        }),
                        [{ field: 'plotTitle.name', dir: 'asc' }],
                    ),
                );
            })
            .catch(() => {
                NotificationService.showNotification(t('errorMessage'));
            });

    useEventBus<IRefreshGridEventBusMessage>(REFRESH_GRID, (refresh: boolean) => {
        if (refresh) {
            (async () => {
                await getCmPanels(risk?.associatedControlsHubPanelIds ?? []);
            })().catch((err) => window.console.error(err));
        }
    });

    useEffect(() => {
        (async () => {
            await getCmPanels(risk?.associatedControlsHubPanelIds ?? []);
        })().catch((err) => window.console.error(err));
    }, [risk?.associatedControlsHubPanelIds]);

    const openAssignOrAddDialog = async () => {
        if (!study || !risk) return;
        try {
            setShowAssignCmChartsDialog(true);
        } catch {
            ShowError(tCommon('errorMessage'));
        }
    };

    const handleAddClick = async () => {
        if (!risk) return;
        await openAssignOrAddDialog();
    };

    const handleAssignCmCharts = async (selectedCmChartIds: string[], close: boolean) => {
        setFilterState({ logic: 'and', filters: [] });
        await getCmPanels([...(risk?.associatedControlsHubPanelIds ?? []), ...selectedCmChartIds]);
        if (close) setShowAssignCmChartsDialog(false);
    };

    const onSubmitUnassignControl = (result: string) => {
        if (result === 'success') ShowSuccess(tCommon('actionsSuccessfulToast'));
        else ShowError(tCommon('errorMessage'));
        showUnassignCmControlDialog.setFalse();
    };

    const columns: GridColumnProps[] = [
        {
            id: 'riskId',
            field: 'riskRecordId',
            title: t('grids.cmControls.columns.riskId'),
            lockedByDefault: true,
            width: 100,
            customColumn: true,
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
            alwaysExportColumn: true,
            showByDefault: false,
        },
        {
            id: 'statement',
            field: 'riskStatement',
            title: t('grids.cmControls.columns.statement'),
            lockedByDefault: true,
            width: 250,
            customColumn: true,
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
            alwaysExportColumn: true,
            showByDefault: false,
        },
        {
            id: 'plotTitle',
            field: 'plotTitle',
            title: t('grids.cmControls.columns.plotTitle'),
            lockedByDefault: true,
            width: 1,
            minWidth: 250,
            filter: 'text',
            filterKey: 'name',
            cells: {
                data: ActiveInactiveCell({
                    inactive: (dataItem: INamedItem) => dataItem?.active === false,
                    renderValue: (dataItem: INamedItem) => dataItem?.name,
                }),
            },
        },
        {
            id: 'dataSet',
            field: 'dataSet',
            title: t('grids.cmControls.columns.dataset'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            width: 1,
            minWidth: 150,
            cellName: 'DateCell',
        },
        {
            id: 'timePeriod',
            field: 'timePeriod',
            title: t('grids.cmControls.columns.timePeriod'),
            filter: 'date',
            cells: { data: DateTimeCell('DD-MMM-yyyy') },
            width: 1,
            minWidth: 150,
            cellName: 'DateCell',
        },
        {
            width: 1,
            minWidth: 150,
            id: 'status',
            field: 'status',
            title: t('grids.cmControls.columns.status'),
            lovData: chips.map((c) => c.value),
            cells: { data: ChipCell(chips, { width: 95 }) },
        },
        {
            width: 1,
            minWidth: 250,
            id: 'yAxisLabel',
            field: 'yAxisLabel',
            title: t('grids.cmControls.columns.yAxisLabel'),
            filter: 'text',
            cells: {
                data: TooltipCell({
                    isCsv: false,
                }),
            },
        },
        {
            width: 1,
            minWidth: 150,
            id: 'ootValue',
            field: 'ootValue',
            title: t('grids.cmControls.columns.ootValue'),
            filter: 'numeric',
        },
        {
            id: 'toleranceLimits',
            field: 'toleranceLimits',
            sortable: false,
            title: t('grids.cmControls.columns.toleranceLimits'),
            children: [
                {
                    width: 1,
                    minWidth: 100,
                    id: 'benchmark',
                    field: 'benchmark',
                    title: t('grids.cmControls.columns.benchmark'),
                    filter: 'numeric',
                },
                {
                    width: 1,
                    minWidth: 100,
                    id: 'upperRed',
                    field: 'upperRed',
                    title: t('grids.cmControls.columns.upperRed'),
                    filter: 'numeric',
                },
                {
                    width: 1,
                    minWidth: 100,
                    id: 'upperAmber',
                    field: 'upperAmber',
                    title: t('grids.cmControls.columns.upperAmber'),
                    filter: 'numeric',
                },
                {
                    width: 1,
                    minWidth: 100,
                    id: 'lowerAmber',
                    field: 'lowerAmber',
                    title: t('grids.cmControls.columns.lowerAmber'),
                    filter: 'numeric',
                },
                {
                    width: 1,
                    minWidth: 100,
                    id: 'lowerRed',
                    field: 'lowerRed',
                    title: t('grids.cmControls.columns.lowerRed'),
                    filter: 'numeric',
                },
            ],
        },
        {
            width: 1,
            minWidth: 100,
            id: 'id',
            field: 'id',
            title: 'ID',
            showByDefault: false,
        },
    ];

    return (
        <div style={{ height: '100%' }} className="remove-card-padding">
            <Grid
                excludeHiddenRowMenuActions
                title={t('grids.cmControls.title')}
                maxHeight="100%"
                testDataId="risklog-cm-controls-grid"
                data={panels}
                columns={columns}
                filterable
                showAddIcon
                addCallback={handleAddClick}
                addRecordTooltip={t('grids.cmControls.menuItems.addRecordLabel')}
                useLoadingOverlay
                canEdit={canEdit}
                loading={isMutating}
                scrollable="virtual"
                dataItemKey="id"
                exportFilename={`${exportFileName}_${study?.studyNumber}_${stringDateWithoutTimeFromCurrentUtcDate(
                    'DD-MMM-YYYY',
                )}`}
                service={AuditServiceType.CORE}
                area={AuditApplicationArea.RISK_ASSESSMENT}
                dataType={AuditDataType.EXCEL_EXPORT}
                removeCardBorder
                rowMenuActions={[
                    {
                        id: 1,
                        label: t('grids.cmControls.actions.popOut'),
                        onClick: (dataItem) => {
                            const chart = dataItem as ControlsHubPanelsAssociatedToRisk;
                            const chartIdRef = chart.panelRef;
                            if (chartIdRef) {
                                const chartViewRoute = AllPaths.path('ra-risk-log-cmcontrol-chart-popout', {
                                    studyId: study?.id,
                                    chartId: chartIdRef,
                                });
                                PopOutPage(user?.organisationKey as string, chartViewRoute);
                            }
                        },
                        disabled: !canView,
                        icon: ['far', 'arrow-up-right-from-square'],
                    },
                    {
                        id: 2,
                        label: t('grids.cmControls.actions.unAssign'),
                        onClick: (dataItem) => {
                            const control = dataItem as ControlsHubPanelsAssociatedToRisk;
                            setSelectedControl(control);
                            showUnassignCmControlDialog.setTrue();
                        },
                        icon: ['fal', 'link-slash'] as IconProp,
                        disabled: !canEdit,
                    },
                ]}
                filter={filterState}
                persistentStorageKey={String(PersistentStorageKeys['risk-log-cm-controls'])}
            />
            {showAssignCmChartsDialog && risk && !cmControlChartsLoading && (
                <AssignCmControlChartsToRiskDialog
                    risk={risk}
                    assignableCmControlCharts={
                        getCmControlChartsForRisk?.getControlChartsForRisk?.filter(
                            (chart) => !risk?.associatedControlsHubPanelIds?.includes(chart?.id),
                        ) ?? []
                    }
                    onClose={() => {
                        setShowAssignCmChartsDialog(false);
                    }}
                    onSubmit={handleAssignCmCharts}
                />
            )}
            {showUnassignCmControlDialog.value && selectedControl && current?.selectionState?.selectedItem && (
                <UnassignCmControlDialog
                    onSubmit={onSubmitUnassignControl}
                    onClose={() => showUnassignCmControlDialog.setFalse()}
                    control={selectedControl as ControlsHubPanelsAssociatedToRisk}
                    risk={current?.selectionState.selectedItem}
                />
            )}
        </div>
    );
};

export default CmControlsArea;
